import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, share } from 'rxjs/operators';

import { ApiService } from '../api-service/api.service';
import { ActionList } from './action-list';
import {
  ActionListAccountsResponse,
  ActionListHistoryItem,
  ActionListHistoryResponse,
  PagedResponse,
} from './action-lists.api.service.response';

const DEFAULT_PAGE_SIZE = 25;

export type ProductStatus = 'Active' | 'Inactive' | 'Demo';

export interface DateRange {
  start: string; // eg "2017-04-07"
  end: string; // eg "2017-04-27"
}

export interface Product {
  productId: string;
  status: ProductStatus;
}

export interface AddAccountsToListFilters {
  salesPerson?: string;
  market?: string;
  searchTerm?: string;
  statusActive?: boolean;
  statusSuspended?: boolean;
  rmPaidFlag?: boolean;
  rmDemoFlag?: boolean;
  rmInactiveFlag?: boolean;
  msPaidFlag?: boolean;
  msInactiveFlag?: boolean;
  smPaidFlag?: boolean;
  smInactiveFlag?: boolean;
  ldPaidFlag?: boolean;
  ldInactiveFlag?: boolean;
  ldRenewFlag?: boolean;
  ldExpiredFlag?: boolean;
  lspActiveFlag?: boolean;
  lspInactiveFlag?: boolean;
  lspAutoRenewFlag?: boolean;
  lspNotAutoRenewFlag?: boolean;
  lspExpiredFlag?: boolean;
  tag?: string[];
  products?: Product[];
  dateRangePicker?: DateRange;
  showDeleted?: boolean;
}

export interface HistoryItemGetter {
  getHistoryItem(historyItemId: string): Observable<ActionListHistoryItem>;
}

export interface RestrictionsResponse {
  allowActivateProduct: boolean;
  allowChangeMarket: boolean;
  allowCreateBrand: boolean;
}

@Injectable({ providedIn: 'root' })
export class ActionListsApiService implements HistoryItemGetter {
  private lookupUrl = `/_ajax/v1/action-lists/get/`;
  private deleteUrl = `/_ajax/v1/action-lists/delete/`;
  private createUrl = `/_ajax/v1/action-lists/create/`;
  private addAccountUrl = `/_ajax/v1/action-lists/add-account/`;
  private addAccountsUrl = `/_ajax/v1/action-lists/add-accounts/`;
  private getRestrictionsUrl = `/_ajax/v1/action-lists/get-restrictions/`;
  private getUrl = `/_ajax/v1/action-list/get/`;
  private getAccountsUrl = `/_ajax/v1/action-lists/accounts/`;
  private removeAccountUrl = `/_ajax/v1/action-lists/remove-account/`;
  private actionListHistoryUrl = '/_ajax/v1/action-lists/history/';
  private addErrorAccountsToListUrl = '/_ajax/v1/action-lists/create-list-for-error/';
  private getHistoryItemUrl = '/_ajax/v1/action-lists/history-item/';

  constructor(private apiService: ApiService) {}

  public deleteActionList(actionListId: string): Observable<any> {
    return this.apiService.post(this.deleteUrl, {
      listId: actionListId,
    });
  }

  public lookupActionLists(
    page = 0,
    marketIds: string[],
    pageSize: number = DEFAULT_PAGE_SIZE,
    searchString: string = null,
  ): Observable<PagedResponse> {
    const params = {
      page: page,
      querySize: pageSize,
      marketIds: marketIds,
    };

    const SEARCH_STRING_KEY = 'searchString';
    if (searchString) {
      params[SEARCH_STRING_KEY] = searchString;
    }

    return this.apiService.post(this.lookupUrl, params).pipe(map(this.handleLookupResponse), share());
  }

  public createActionList(listName: string): Observable<ActionList> {
    return this.apiService
      .post(this.createUrl, {
        listName: listName,
      })
      .pipe(map((response) => new ActionList(response)));
  }

  public addAccountToActionList(listName: string, accountGroupId: string): Observable<any> {
    const body = {
      accountGroupId: accountGroupId,
      actionList: listName,
    };
    return this.apiService.post(this.addAccountUrl, body);
  }

  public addAccountsToActionList(listName: string, accountGroupIds: string[]): Observable<any> {
    const body = {
      actionList: listName,
      accountGroupIds: accountGroupIds,
    };
    return this.apiService.post(this.addAccountsUrl, body);
  }

  private handleLookupResponse(response: any): PagedResponse {
    if (!response.actionLists) {
      return {
        actionLists: [],
        nextPage: 0,
        hasMore: false,
        totalActionListsCount: 0,
      };
    }
    const actionLists: ActionList[] = [];
    response.actionLists.forEach((actionList) => {
      actionLists.push(new ActionList(actionList));
    });
    return {
      actionLists: actionLists,
      nextPage: response.nextPage || 0,
      hasMore: response.nextPage > 0,
      totalActionListsCount: response.totalActionListsCount || 0,
    };
  }

  public getRestrictions(): Observable<RestrictionsResponse> {
    return this.apiService.get(this.getRestrictionsUrl);
  }

  public getActionList(actionListId: string): Observable<any> {
    const options = {
      params: {
        actionListId: actionListId,
      },
    };
    return this.apiService.get(this.getUrl, options);
  }

  public getActionListAccounts(actionListId: string, page = 0): Observable<ActionListAccountsResponse> {
    const options = {
      params: {
        listId: actionListId,
        page: page,
      },
    };
    return this.apiService.get(this.getAccountsUrl, options);
  }

  public removeAccount(accountGroupId: string, actionListId: string): Observable<boolean> {
    const body = {
      accountGroupId: accountGroupId,
      listId: actionListId,
    };
    return this.apiService.post(this.removeAccountUrl, body);
  }

  public getActionListHistory(listId: string, cursor = ''): Observable<ActionListHistoryResponse> {
    if (cursor === null) {
      cursor = '';
    }
    const options = {
      params: {
        nextCursor: cursor,
      },
    } as any;

    if (listId.startsWith('G-')) {
      options.params.groupId = listId;
    } else {
      options.params.actionListId = listId;
    }

    return this.apiService.get(this.actionListHistoryUrl, options);
  }

  public addErrorAccountToList(actionListName: string, historyItemId: string, errorReason: string): Observable<any> {
    const body = {
      errorReason: errorReason,
      historyItemId: historyItemId,
      actionListName: actionListName,
    };
    return this.apiService.post(this.addErrorAccountsToListUrl, body);
  }

  getHistoryItem(historyItemId: string): Observable<ActionListHistoryItem> {
    const options = {
      params: {
        itemId: historyItemId,
      },
    };
    return this.apiService.get(this.getHistoryItemUrl, options);
  }
}
